import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LeftIcon from '../../images/LeftIcon';
import ArrowRightIcon from '../../images/ArrowRightIcon';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import PopupLocation from '../../components/PopupLocation';
import { validateNoSpecialChars } from '../../validation/ValidationInput';
import AttributesIcon from '../../images/AttributesIcon';
import SearchIcon from '../../images/SearchIcon';
import WhiteCheckIcon from '../../images/WhiteCheckIcon';
import {
  AttributesPayload,
  createAttributes,
  getDetailAttributes,
  getListSubAttributes,
  ProductAttributesPayload,
  updateAttributes,
} from '../../services/attributes.service';
import DropdownIcon from '../../images/DropdownIcon';
import WhiteDropdownIcon from '../../images/WhiteDropdownIcon';
import { useInfiniteScroll } from '../../hook/useInfiniteScroll';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import UploadIcon from '../../images/UploadIcon';
import CloseIcon from '../../images/CloseIcon';
import CloseIconV2 from '../../images/CloseIconV2';
import { getListProductAttributes } from '../../services/inventoryCorrections.service';
import CheckIcon from '../../images/CheckIcon';
import BackIcon from '../../images/BackIcon';
import { DropdownSearch } from '../../components/DropdownSearch';
import PhotoIcon from '../../images/PhotoIcon';
import { useResponsive } from '../../hook/useResponsive';
import { useDebounceEffect } from '../../hook/useDebounceEffect';
import { useDialogFocus, useFormKeyboard } from '../../hook/useFormKeyboard';
import { t } from '../../components/list';
import {
  BaseAttributeSheet,
  ColorRow,
  ColorSheet,
  ImagePreviewOverlay,
  LogoRow,
  MappingsToolbar,
  MobileField,
  MobileHeader,
  MobileTextInput,
  PickerRow,
  ProductCard,
  Segmented,
  StickyFooter,
} from './createAttributesMobile';

interface TermsResponse {
  attributeName: string;
  displayName: string;
  type: number;
  baseAttributeId?: string;
  image?: string;
  /** Hex #RRGGBB; empty when not customizing POS appearance */
  backgroundColor?: string;
  fontColor?: string;
}

function normalizeHex(raw: string): string {
  let v = raw.trim();
  if (!v) return '';
  if (!v.startsWith('#')) v = '#' + v;
  if (/^#[0-9A-Fa-f]{6}$/i.test(v)) return v.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/i.test(v)) {
    const h = v.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toUpperCase();
  }
  return v;
}

function parsePosColorsFromDetail(d: any): { backgroundColor: string; fontColor: string } {
  const bg = d?.backgroundColor ?? d?.background_color ?? '';
  const fc = d?.fontColor ?? d?.font_color ?? '';
  return {
    backgroundColor: typeof bg === 'string' && bg ? normalizeHex(bg) : '',
    fontColor: typeof fc === 'string' && fc ? normalizeHex(fc) : '',
  };
}

/** Chỉ lấy tối đa một base attribute từ chi tiết API (create/edit). */
function parseSingleBaseAttributeFromDetail(d: any): BaseAttributeOption | null {
  const listFromApi =
    d?.subAttributes ?? d?.sub_attributes ?? d?.baseAttributes ?? d?.base_attributes ?? null;
  const arr = Array.isArray(listFromApi) ? listFromApi : [];
  if (arr.length > 0) {
    const item = arr[0];
    const sid = String(item?.id ?? '');
    const name = item?.attributeName ?? item?.displayName ?? item?.name ?? '';
    if (sid) {
      return { id: sid, attributeName: name || sid };
    }
  }
  const legacyId = d?.baseAttributeId ?? d?.base_attribute_id;
  const ba = d?.baseAttribute;
  const legacyName =
    (typeof ba === 'object' && ba
      ? ba.attributeName ?? ba.displayName ?? ba.name
      : undefined) ??
    d?.baseAttributeName ??
    '';
  if (legacyId) {
    return {
      id: String(legacyId),
      attributeName: legacyName || String(legacyId),
    };
  }
  return null;
}

/** Fallback hiển thị khi chưa lưu màu POS — trùng tone với form (logo circle / heading). */
const DEFAULT_POS_BG = '#EBF0F0';
const DEFAULT_POS_FONT = '#05292A';

interface Product {
  id: string;
  sku: string;
  type: number;
  productName: string;
  productGroup: { groupName: string };
  updatedAt: Date;
  isActive: boolean;
  isAttributes: true | false;
}

interface ProductResponse {
  data: {
    productWithCompany: Product[];
    count: number;
  };
}

interface ProductAttributesResponse {
  id: string;
  position: number;
}

interface BaseAttributeOption {
  id: string;
  attributeName: string;
}

function normalizeBaseAttributeOptions(list: any[]): BaseAttributeOption[] {
  if (!Array.isArray(list)) return [];
  return list
    .map((item: any) => ({
      id: String(item?.id ?? ''),
      attributeName: item?.attributeName ?? item?.displayName ?? item?.name ?? '',
    }))
    .filter((item: BaseAttributeOption) => item.id && item.attributeName);
}
// Fields with a server limit; all sit on step 1, so a save error jumps back there.
const ATTRIBUTE_FIELD_STEPS: Record<string, number> = { attributeName: 1, displayName: 1, type: 1 };
const CreateAttribuites: React.FC = () => {
  const limits = useFieldLimits(inventoryFormLimits('attribute'));
  const typeRef = useRef<HTMLDivElement>(null);
  const baseAttributeRef = useRef<HTMLDivElement>(null);
  const filterCorrectionsRef = useRef<HTMLDivElement>(null);
  const preLanguage = localStorage.getItem('language');
  const [step, setStep] = useState<number>(1);
  const initalFilter = {
    searchValue: '',
    page: 1,
    limit: 50,
  };
  const { isMobile, getWidth } = useResponsive();
  const [filter, setFilter] = useState<any>(initalFilter);
  const [hasMore, setHasMore] = useState({
    product: true,
    attributes: true,
  });
  const [loading, setLoading] = useState({
    product: false,
    attributes: false,
  });
  const [filterType, setFilterType] = useState('All');
  const [listProducts, setListProducts] = useState<ProductResponse['data']['productWithCompany']>(
    []
  );
  const [listRawProducts, setListRawProducts] = useState<
    ProductResponse['data']['productWithCompany']
  >([]);
  const [listProductAttributes, setListProductAttributes] = useState<ProductAttributesResponse[]>(
    []
  );
  const companyId = localStorage.getItem('companyId') || '';
  const [isFilterTypeDropdownOpen, setIsFilterTypeDropdownOpen] = useState(false);
  const [filterTypeHighlight, setFilterTypeHighlight] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [type, setType] = useState('');
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isBaseAttributeDropdownOpen, setIsBaseAttributeDropdownOpen] = useState(false);
  const [language, setLanguage] = useState<any>(null);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [fileUpload, setFileUpload] = useState<any>('');
  const [showPopupImage, setShowPopupImage] = useState<boolean>(false);
  const [previewImg, setPreviewImg] = useState<any>('');
  const [baseAttributes, setBaseAttributes] = useState<BaseAttributeOption[]>([]);
  const [baseAttributeValue, setBaseAttributeValue] = useState('');
  const [loadingBaseAttributes, setLoadingBaseAttributes] = useState(false);
  const [baseAttributePage, setBaseAttributePage] = useState(1);
  const [baseAttributeHasMore, setBaseAttributeHasMore] = useState(true);
  const [baseAttributeKeyword, setBaseAttributeKeyword] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Mobile-only UI state: picker sheets, inline validation, pinned mappings. */
  const [isBaseSheetOpen, setIsBaseSheetOpen] = useState(false);
  const [colorSheetField, setColorSheetField] = useState<'backgroundColor' | 'fontColor' | null>(
    null
  );
  const [showMobileErrors, setShowMobileErrors] = useState(false);
  const mobileFieldRefs = {
    attributeName: useRef<HTMLDivElement>(null),
    type: useRef<HTMLDivElement>(null),
  };
  const pinnedProductIdsRef = useRef<Set<string>>(new Set());
  /** Keyword the current product list was fetched with (skips the redundant page-1 refetch on entering step 2). */
  const loadedKeywordRef = useRef<string | null>(null);

  const listTypeFilter = [
    { id: 1, name: 'All', value: 'All' },
    { id: 2, name: 'Checked', value: 'Checked' },
  ];
  const listPayment = [
    { id: 1, name: 'Sales', value: 1 },
    { id: 2, name: 'Stock ', value: 2 },
  ];
  const [formValues, setFormValues] = useState<TermsResponse>({
    attributeName: '',
    displayName: '',
    type: 1,
    baseAttributeId: '',
    backgroundColor: '',
    fontColor: '',
  });
  /** Chi tiết từ API đã có màu POS — luôn gửi lại khi lưu (kể cả không sửa). */
  const [hasPosColorsFromApi, setHasPosColorsFromApi] = useState(false);
  /** Người dùng đã chỉnh màu — gửi payload màu (create mới hoặc đổi màu). */
  const [posColorsTouched, setPosColorsTouched] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const path = window.location.pathname;
  const id: string = path.split('/').pop() || '';
  const kb = useFormKeyboard({ step, autoFocus: id === 'new' && !isMobile });
  const imageDialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(showPopupImage, imageDialogRef, () => setShowPopupImage(false));

  useEffect(() => {
    if (!isFilterTypeDropdownOpen) setFilterTypeHighlight(-1);
  }, [isFilterTypeDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeRef.current && !typeRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false);
      }
      if (
        baseAttributeRef.current &&
        !baseAttributeRef.current.contains(event.target as Node)
      ) {
        setIsBaseAttributeDropdownOpen(false);
      }
      if (
        filterCorrectionsRef.current &&
        !filterCorrectionsRef.current.contains(event.target as Node)
      ) {
        setIsFilterTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormValues(prevValues => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handlePosColorPicker =
    (field: 'backgroundColor' | 'fontColor') => (e: React.ChangeEvent<HTMLInputElement>) => {
      setPosColorsTouched(true);
      setFormValues(prev => ({
        ...prev,
        [field]: e.target.value.toUpperCase(),
      }));
    };

  const handlePosColorHexInput =
    (field: 'backgroundColor' | 'fontColor') => (e: React.ChangeEvent<HTMLInputElement>) => {
      setPosColorsTouched(true);
      setFormValues(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handlePosColorHexBlur = (field: 'backgroundColor' | 'fontColor') => () => {
    setPosColorsTouched(true);
    setFormValues(prev => {
      const raw = (prev[field] as string) || '';
      const n = normalizeHex(raw);
      if (!n) {
        return {
          ...prev,
          [field]: field === 'backgroundColor' ? DEFAULT_POS_BG : DEFAULT_POS_FONT,
        };
      }
      return { ...prev, [field]: n };
    });
  };

  /** Captured once per mount: a resize mid-list must never mix page sizes. */
  const pageSizeRef = useRef<number | null>(null);
  if (pageSizeRef.current === null) pageSizeRef.current = isMobile ? 25 : 20;
  const PAGE_SIZE = pageSizeRef.current;
  const MAPPINGS_SEARCH_DEBOUNCE_MS = 300;

  const hasLoadedPageRef = useRef<Set<number>>(new Set());
  /** Trang tiếp theo cần gọi API (cập nhật đồng bộ sau mỗi lần thành công — tránh stale `filter.page` chặn nhầm load trang 2). */
  const nextPageToFetchRef = useRef(1);
  const listProductAttributesRef = useRef(listProductAttributes);
  listProductAttributesRef.current = listProductAttributes;

  const onLoadMore = async () => {
    if (loading.product) return;
    const pageToFetch = nextPageToFetchRef.current;
    if (hasLoadedPageRef.current.has(pageToFetch)) return;

    hasLoadedPageRef.current.add(pageToFetch);

    setLoading(prev => ({ ...prev, product: true }));

    try {
      let productSelect: ProductAttributesResponse[] = listProductAttributes;
      if (pageToFetch === 1 && id !== 'new') {
        webLoadingTrue();
        try {
          const response: any = await getDetailAttributes(id, companyId);
          const d = response.data.data;
          const posColors = parsePosColorsFromDetail(d);
          const baseOne = parseSingleBaseAttributeFromDetail(d);
          setFormValues({
            attributeName: d.attributeName,
            displayName: d.displayName,
            type: d.type,
            baseAttributeId: baseOne?.id ?? '',
            image: d.image,
            backgroundColor: posColors.backgroundColor,
            fontColor: posColors.fontColor,
          });
          setBaseAttributeValue(baseOne?.attributeName ?? '');
          setHasPosColorsFromApi(!!(posColors.backgroundColor || posColors.fontColor));
          setPosColorsTouched(false);
          setListProductAttributes(response.data.data.products);
          setType(response.data.data.type === 1 ? 'Sales' : 'Stock');
          productSelect = response.data.data.products;
        } catch (error) {
          console.error('Error loading attributes:', error);
        } finally {
          webLoadingFalse();
        }
      }
      const response: ProductResponse = await getListProductAttributes(
        pageToFetch,
        PAGE_SIZE,
        filter.searchValue,
        id !== 'new' ? id : ''
      );

      const fetchedProducts = response.data.productWithCompany;
      const updatedProducts = fetchedProducts.map((value, index) => ({
        ...value,
        position: (pageToFetch - 1) * PAGE_SIZE + index + 1,
        isAttributes: id !== 'new' && productSelect?.some(val => val.id === value.id),
      }));
      setListProducts(prev => [...prev, ...updatedProducts]);
      setListRawProducts(prev => [...prev, ...updatedProducts]);
      if (pageToFetch === 1) loadedKeywordRef.current = filter.searchValue?.trim() ?? '';
      nextPageToFetchRef.current = pageToFetch + 1;
      setFilter((prev: any) => ({ ...prev, page: pageToFetch + 1 }));
      if (fetchedProducts.length < PAGE_SIZE) {
        setHasMore(prev => ({ ...prev, product: false }));
      }
    } catch (err) {
      console.error(err);
      hasLoadedPageRef.current.delete(pageToFetch);
    } finally {
      setLoading(prev => ({ ...prev, product: false }));
    }
  };

  const { setLoaderRef } = useInfiniteScroll({
    hasMore: hasMore.product,
    loading: loading.product,
    onLoadMore: onLoadMore,
    scrollRootRef: scrollRef,
  });

  useEffect(() => {
    nextPageToFetchRef.current = 1;
    hasLoadedPageRef.current.clear();
    setFilter((prev: any) => ({ ...initalFilter, searchValue: prev.searchValue }));
    setHasMore(prev => ({ ...prev, product: true }));

    if (id === 'new') {
      fetchProducts('', undefined, 1);
    }
    if (id !== 'new') {
      setListProducts([]);
      setListRawProducts([]);
      onLoadMore();
    }
    const handleNewToken = () => {
      nextPageToFetchRef.current = 1;
      hasLoadedPageRef.current.clear();
      setFilter((prev: any) => ({ ...initalFilter, searchValue: prev.searchValue }));
      setHasMore(prev => ({ ...prev, product: true }));
      if (id !== 'new') {
        setListProducts([]);
        setListRawProducts([]);
        onLoadMore();
      }
      if (id === 'new') {
        fetchProducts('', undefined, 1);
      }
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, [path]);

  useEffect(() => {
    setPosColorsTouched(false);
    setHasPosColorsFromApi(false);
    if (id === 'new') {
      setFormValues(prev => ({
        ...prev,
        backgroundColor: '',
        fontColor: '',
        baseAttributeId: '',
      }));
      setBaseAttributeValue('');
    }
  }, [id, path]);

  const loadBaseAttributes = useCallback(async (searchValue = '', page = 1) => {
    setLoadingBaseAttributes(true);
    try {
      const response: any = await getListSubAttributes(
        companyId,
        page,
        PAGE_SIZE,
        searchValue,
        id === 'new' ? null : id
      );
      const listFromApi = response?.data?.data?.attributes || response?.data?.data || [];
      const normalizedList = normalizeBaseAttributeOptions(listFromApi);
      setBaseAttributes(prev => {
        if (page === 1) {
          return normalizedList;
        }
        const merged = [...prev, ...normalizedList];
        const seen = new Set<string>();
        return merged.filter(item => {
          const key = String(item.id);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
      setBaseAttributePage(page);
      setBaseAttributeHasMore(normalizedList.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Error loading base attributes:', error);
    } finally {
      setLoadingBaseAttributes(false);
    }
  }, [companyId, id]);

  const handleSearchBaseAttributes = useCallback(
    async (searchValue: string) => {
      setBaseAttributeKeyword(searchValue);
      setBaseAttributeHasMore(true);
      await loadBaseAttributes(searchValue, 1);
    },
    [loadBaseAttributes]
  );

  const handleLoadMoreBaseAttributes = useCallback(async () => {
    if (loadingBaseAttributes || !baseAttributeHasMore) return;
    await loadBaseAttributes(baseAttributeKeyword, baseAttributePage + 1);
  }, [
    baseAttributeHasMore,
    baseAttributeKeyword,
    baseAttributePage,
    loadBaseAttributes,
    loadingBaseAttributes,
  ]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const fetchProducts = async (
    value: string,
    listProductAttributes?: ProductAttributesResponse[],
    pageOverride?: number
  ) => {
    try {
      const pageToUse = pageOverride ?? filter.page;
      const keyword = value ?? '';
      const selectedProducts = listProductAttributes ?? [];
      const response: ProductResponse = await getListProductAttributes(
        pageToUse,
        PAGE_SIZE,
        keyword,
        id !== 'new' ? id : ''
      );
      const dataArray = response.data.productWithCompany.map(val => {
        return {
          ...val,
          isAttributes: selectedProducts.some((item: ProductAttributesResponse) => item.id === val.id),
        };
      });
      setListProducts(dataArray);
      setListRawProducts(dataArray);
      if (pageToUse === 1) loadedKeywordRef.current = keyword.trim();
      nextPageToFetchRef.current = pageToUse + 1;
      hasLoadedPageRef.current.add(pageToUse);
      setHasMore(prev => ({ ...prev, product: response.data.productWithCompany.length >= PAGE_SIZE }));
      setFilter((prev: any) => ({ ...prev, page: pageToUse + 1, searchValue: keyword }));
    } catch (err) {
      console.log('🚀 ~ fetchProducts ~ err:', err);
    }
  };

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  useEffect(() => {
    setBaseAttributeKeyword('');
    setBaseAttributePage(1);
    setBaseAttributeHasMore(true);
    loadBaseAttributes('', 1);
  }, [companyId, loadBaseAttributes]);

  const selectedBaseId = formValues.baseAttributeId || '';

  const availableBaseAttributes = useMemo(
    () =>
      baseAttributes.filter(
        item =>
          (id === 'new' || String(item.id) !== String(id)) &&
          String(item.id) !== String(selectedBaseId ?? '')
      ),
    [baseAttributes, id, selectedBaseId]
  );

  const clearBaseAttributeSelection = () => {
    setFormValues(prev => ({ ...prev, baseAttributeId: '' }));
    setBaseAttributeValue('');
    setBaseAttributeKeyword('');
    setBaseAttributePage(1);
    setBaseAttributeHasMore(true);
    loadBaseAttributes('', 1);
  };
  const handleClosePopup = () => {
    setShowPopup(false);
  };
  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  /** Step 2 → Step 1; Step 1 → danh sách Attributes (đồng bộ header + footer Cancel/Back). */
  const handleBackNavigation = () => {
    if (step === 2) {
      setStep(1);
    } else {
      webNavigate('/' + preLanguage + '/attributes');
    }
  };

  const handleTypeSelect = (name: string, value: number) => {
    setFormValues((prev: any) => ({ ...prev, type: value }));
    setType(name);
    setIsTypeDropdownOpen(false);
  };

  const handleSelectProduct = (value: Product) => {
    const updatedProducts = [...listProducts];
    const index = updatedProducts.findIndex(product => product.id === value.id);
    const selectedProduct = updatedProducts[index];
    selectedProduct.isAttributes = !selectedProduct.isAttributes;
    const exists = listProductAttributes.some(attr => attr.id === selectedProduct.id);
    const updatedAttributes = exists
      ? listProductAttributes.filter(attr => attr.id !== selectedProduct.id)
      : [...listProductAttributes, { id: selectedProduct.id, position: index + 1 }];
    setListProducts(updatedProducts);
    setListRawProducts(updatedProducts);
    setListProductAttributes(updatedAttributes);
  };

  const handleSubmitSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const keyword = filter.searchValue?.trim() ?? '';
    if (event.key === 'Enter' && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      nextPageToFetchRef.current = 1;
      hasLoadedPageRef.current.clear();
      setHasMore(prev => ({ ...prev, product: true }));
      fetchProducts(keyword, listProductAttributesRef.current, 1);
    }
  };

  useDebounceEffect(
    () => {
      if (step !== 2) return;
      const keyword = filter.searchValue?.trim() ?? '';
      if (loadedKeywordRef.current === keyword && hasLoadedPageRef.current.has(1)) return;
      nextPageToFetchRef.current = 1;
      hasLoadedPageRef.current.clear();
      setHasMore(prev => ({ ...prev, product: true }));
      fetchProducts(keyword, listProductAttributesRef.current, 1);
    },
    [filter.searchValue, step, id],
    MAPPINGS_SEARCH_DEBOUNCE_MS
  );
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: any = validateAgainstLimits(formValues as any, limits, language, Object.keys(ATTRIBUTE_FIELD_STEPS));
    if (!formValues.attributeName) {
      newErrors.attributeName = 'Attribute Name is required';
    }
    if (!formValues.type) {
      newErrors.type = 'Type is required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setStep(1);
      return;
    }
    webLoadingTrue();
    const formData = new FormData();
    formData.append('image', fileUpload);
    const payload: AttributesPayload = {
      attributeName: formValues.attributeName,
      displayName: formValues.displayName,
      type: formValues.type,
      companyId,
      baseAttributeId:
        formValues.baseAttributeId && String(formValues.baseAttributeId).trim()
          ? String(formValues.baseAttributeId).trim()
          : null,
      items: listProductAttributes.map(attr => ({
        productId: attr.id,
        position: attr.position,
      })),
    };
    if (formValues.image) {
      payload.image = formValues.image;
    }
    if (hasPosColorsFromApi || posColorsTouched) {
      payload.backgroundColor = normalizeHex(formValues.backgroundColor || DEFAULT_POS_BG);
      payload.fontColor = normalizeHex(formValues.fontColor || DEFAULT_POS_FONT);
    }
    formData.append('payload', JSON.stringify(payload));
    if (id === 'new') {
      try {
        const response: any = await createAttributes(formData);
        if (response) {
          webNavigate('/' + preLanguage + '/attributes');
          webLoadingFalse();
          showToast({
            message: "Attribute Created !",
            type: 'success',
            duration: 20000,
          });
        }
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, { language, setFieldErrors: setErrors, fieldSteps: ATTRIBUTE_FIELD_STEPS, onStep: setStep });
      }
    } else {
      try {
        const response: any = await updateAttributes(id, formData);
        if (response) {
          webNavigate('/' + preLanguage + '/attributes');
          webLoadingFalse();
          showToast({
            message: language?.['Attribute Updated !'] ?? 'Attribute Updated !',
            type: 'success',
            duration: 20000,
          });
        }
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, { language, setFieldErrors: setErrors, fieldSteps: ATTRIBUTE_FIELD_STEPS, onStep: setStep });
      }
    }
  };

  const handleFilterTypeSelect = (paymentName: string) => {
    setFilterType(paymentName);
    setIsFilterTypeDropdownOpen(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const processFile = (file: File) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/heic', 'image/png', 'image/webp'];

    // Kiểm tra loại file
    if (!validTypes.includes(file.type)) {
      setShowPopup(true);
      setErrors((prev: any) => ({
        ...prev,
        missingFields: 'Only .jpeg, .jpg, .heic, .png, .webp files are allowed',
      }));
      return;
    }

    const MAX_FILE_BYTES = 80 * 1024;
    const OUT_W = 800;
    const OUT_H = 450;
    const MIN_W = 320;
    const MIN_H = 180;

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = async () => {
      const aspectRatio = 16 / 9;
      let cropWidth = img.width;
      let cropHeight = img.width / aspectRatio;

      if (cropHeight > img.height) {
        cropHeight = img.height;
        cropWidth = img.height * aspectRatio;
      }

      const startX = (img.width - cropWidth) / 2;
      const startY = (img.height - cropHeight) / 2;

      const toBlobAsync = (canvas: HTMLCanvasElement, quality: number) =>
        new Promise<Blob | null>(resolve =>
          canvas.toBlob(b => resolve(b), 'image/jpeg', quality)
        );

      let canvas = document.createElement('canvas');
      canvas.width = OUT_W;
      canvas.height = OUT_H;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, startX, startY, cropWidth, cropHeight, 0, 0, OUT_W, OUT_H);

      let q = 0.92;
      let blob: Blob | null = await toBlobAsync(canvas, q);
      while (blob && blob.size > MAX_FILE_BYTES && q > 0.45) {
        q -= 0.07;
        blob = await toBlobAsync(canvas, q);
      }
      while (blob && blob.size > MAX_FILE_BYTES && canvas.width > MIN_W && canvas.height > MIN_H) {
        const nw = Math.max(MIN_W, Math.floor(canvas.width * 0.85));
        const nh = Math.max(MIN_H, Math.floor(canvas.height * 0.85));
        const c2 = document.createElement('canvas');
        c2.width = nw;
        c2.height = nh;
        const c2x = c2.getContext('2d');
        if (!c2x) break;
        c2x.drawImage(canvas, 0, 0, nw, nh);
        canvas = c2;
        q = 0.92;
        blob = await toBlobAsync(canvas, q);
        while (blob && blob.size > MAX_FILE_BYTES && q > 0.45) {
          q -= 0.07;
          blob = await toBlobAsync(canvas, q);
        }
      }

      if (!blob) return;
      const outName = file.name.replace(/\.[^.]+$/i, '.jpg');
      const croppedFile = new File([blob], outName, { type: 'image/jpeg' });
      setFileUpload(croppedFile);
      setPreviewImg(URL.createObjectURL(blob));
      setFileName(file.name);
      setErrors((prev: any) => {
        const next = { ...prev };
        delete next.missingFields;
        return next;
      });
    };
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleRemoveFile = () => {
    setFileName('');
    setFileUpload(null);
    setPreviewImg(null);
    setFormValues((prev: any) => ({ ...prev, image: null }));
    let inputHoolder = document.getElementById('fileInput') as HTMLInputElement;
    inputHoolder.value = '';
  };

  /* ---------------- Mobile (< 1024px) ---------------- */

  const mobileStep1Errors = useMemo(() => {
    const next: Record<string, string> = validateAgainstLimits(formValues as any, limits, language, ['attributeName', 'displayName']);
    if (!formValues.attributeName?.trim()) {
      next.attributeName = t(language, 'Attribute Name is required', 'Attribute Name is required');
    }
    if (!formValues.type) {
      next.type = t(language, 'Type is required', 'Type is required');
    }
    return next;
  }, [formValues.attributeName, formValues.displayName, formValues.type, language, limits]);
  const isMobileStep1Valid = Object.keys(mobileStep1Errors).length === 0;

  const revealMobileErrors = () => {
    setShowMobileErrors(true);
    const first = (['attributeName', 'type'] as const).find(
      key => mobileStep1Errors[key]
    );
    if (first) {
      window.setTimeout(() => {
        mobileFieldRefs[first].current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 0);
    }
  };

  const handleMobileNext = () => {
    if (!isMobileStep1Valid) {
      revealMobileErrors();
      return;
    }
    pinnedProductIdsRef.current = new Set(
      listProducts.filter(value => value.isAttributes).map(value => value.id)
    );
    setStep(2);
  };

  const handleMobileSave = (e: React.FormEvent) => {
    if (!isMobileStep1Valid) {
      setStep(1);
      revealMobileErrors();
      return;
    }
    return handleSubmit(e);
  };

  useEffect(() => {
    if (isMobile) scrollRef.current?.scrollTo({ top: 0 });
  }, [step, isMobile]);

  /** Step 2 on mobile: the products assigned when the step was entered stay pinned first. */
  const mobileProducts = useMemo(() => {
    const visible = listProducts.filter(value => filterType === 'All' || value.isAttributes);
    const pinned = pinnedProductIdsRef.current;
    return {
      pinned: visible.filter(value => pinned.has(value.id)),
      rest: visible.filter(value => !pinned.has(value.id)),
    };
    // pinnedProductIdsRef only changes together with `step`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listProducts, filterType, step]);

  const mobileBaseSelected =
    formValues.baseAttributeId && baseAttributeValue
      ? { id: String(formValues.baseAttributeId), attributeName: baseAttributeValue }
      : null;
  const posBackground = normalizeHex(formValues.backgroundColor || DEFAULT_POS_BG) || DEFAULT_POS_BG;
  const posFont = normalizeHex(formValues.fontColor || DEFAULT_POS_FONT) || DEFAULT_POS_FONT;
  const logoSrc = previewImg || formValues?.image || null;

  if (isMobile) {
    const mobileTitle =
      id === 'new'
        ? t(language, 'Add Attributes', 'Add Attributes')
        : t(language, 'Edit Attributes', 'Edit Attributes');
    // Server field errors (set by handleSubmit's catch) show under the same fields.
    const mobileErrors: Record<string, string> = { ...errors, ...(showMobileErrors ? mobileStep1Errors : {}) };
    return (
      <div {...kb.scopeProps}>
      <div
        ref={scrollRef}
        className="bg-white"
        style={{
          height: 'calc(100% + 32px)',
          margin: '-16px',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <MobileHeader
          title={mobileTitle}
          step={step}
          steps={2}
          onBack={handleBackNavigation}
          backLabel={t(language, 'Back', 'Back')}
        />
        {step === 1 ? (
          <form
            onSubmit={e => {
              e.preventDefault();
              handleMobileNext();
            }}
            noValidate
            style={{ padding: '16px 16px 24px', display: 'flex', flexDirection: 'column', gap: '18px' }}
          >
            <MobileField
              ref={mobileFieldRefs.attributeName}
              label={t(language, 'Attribute Name', 'Attribute Name')}
              required
              error={mobileErrors.attributeName}
            >
              <MobileTextInput
                type="text"
                name="attributeName"
                autoComplete="off"
                {...limitProps(limits, 'attributeName')}
                value={formValues.attributeName}
                onChange={handleChange}
                onKeyDown={validateNoSpecialChars}
                invalid={!!mobileErrors.attributeName}
                aria-invalid={!!mobileErrors.attributeName}
              />
              <FieldCounter value={formValues.attributeName} max={limits.attributeName?.maxLength} />
            </MobileField>
            <MobileField
              label={t(language, 'Display name', 'Display name')}
              error={mobileErrors.displayName}
            >
              <MobileTextInput
                type="text"
                name="displayName"
                autoComplete="off"
                {...limitProps(limits, 'displayName')}
                value={formValues.displayName}
                onChange={handleChange}
                invalid={!!mobileErrors.displayName}
              />
              <FieldCounter value={formValues.displayName} max={limits.displayName?.maxLength} />
            </MobileField>
            <MobileField
              ref={mobileFieldRefs.type}
              label={t(language, 'Type', 'Type')}
              error={mobileErrors.type}
            >
              <Segmented<number>
                ariaLabel={t(language, 'Type', 'Type')}
                value={Number(formValues.type) || 0}
                onChange={value =>
                  handleTypeSelect(value === 1 ? 'Sales' : 'Stock', value)
                }
                options={[
                  { value: 1, label: t(language, 'Sales', 'Sales') },
                  { value: 2, label: t(language, 'Stock', 'Stock') },
                ]}
              />
            </MobileField>
            <MobileField label={t(language, 'Base Attribute', 'Base Attribute')}>
              <PickerRow
                ariaLabel={t(language, 'Base Attribute', 'Base Attribute')}
                value={mobileBaseSelected?.attributeName}
                placeholder={t(language, 'Select base attribute', 'Select base attribute')}
                onClick={() => setIsBaseSheetOpen(true)}
              />
            </MobileField>
            <MobileField label={t(language, 'POS appearance', 'POS appearance')}>
              <div
                className="flex items-center"
                style={{
                  padding: '12px 14px',
                  border: '1px solid #DBE4E4',
                  borderRadius: '12px 12px 0 0',
                  borderBottom: 'none',
                  gap: '12px',
                }}
              >
                <span
                  className="font-inter"
                  style={{ fontSize: '12.5px', color: '#6A7878', flex: 'none' }}
                >
                  {t(language, 'POS preview', 'POS preview')}
                </span>
                <span
                  className="inline-block max-w-full rounded-md px-3 py-2 text-[14px] font-inter font-medium leading-[20px]"
                  style={{
                    backgroundColor: posBackground,
                    color: posFont,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formValues.displayName?.trim() || t(language, 'Display name', 'Display name')}
                </span>
              </div>
              <ColorRow
                label={t(language, 'Background', 'Background')}
                value={posBackground}
                radius="0"
                onClick={() => setColorSheetField('backgroundColor')}
              />
              <div style={{ marginTop: '-1px' }}>
                <ColorRow
                  label={t(language, 'Text', 'Text')}
                  value={posFont}
                  radius="0 0 12px 12px"
                  onClick={() => setColorSheetField('fontColor')}
                />
              </div>
            </MobileField>
            <MobileField label={t(language, 'Logo', 'Logo')}>
              <LogoRow
                language={language}
                src={logoSrc}
                fileName={fileName}
                inputId="fileInput"
                onFileChange={handleFileChange}
                onPreview={() => setShowPopupImage(true)}
                onRemove={handleRemoveFile}
              />
            </MobileField>
            {/* Enter on a text field submits (= Next) */}
            <button type="submit" style={{ display: 'none' }} aria-hidden tabIndex={-1} />
          </form>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}>
            <MappingsToolbar
              language={language}
              inputRef={searchInputRef}
              search={filter.searchValue}
              onSearchChange={value => setFilter((prev: any) => ({ ...prev, searchValue: value }))}
              onSearchKeyDown={handleSubmitSearch}
              filter={filterType === 'Checked' ? 'Checked' : 'All'}
              onFilterChange={handleFilterTypeSelect}
              selectedCount={listProductAttributes.length}
            />
            <div style={{ padding: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {mobileProducts.pinned.length > 0 && (
                <p
                  className="font-inter"
                  style={{ fontSize: '12.5px', fontWeight: 500, color: '#6A7878', padding: '4px 2px' }}
                >
                  {t(language, 'Assigned', 'Assigned')}
                </p>
              )}
              {mobileProducts.pinned.map(value => (
                <ProductCard
                  key={value.id}
                  product={value}
                  language={language}
                  onToggle={() => handleSelectProduct(value)}
                />
              ))}
              {mobileProducts.pinned.length > 0 && mobileProducts.rest.length > 0 && (
                <p
                  className="font-inter"
                  style={{ fontSize: '12.5px', fontWeight: 500, color: '#6A7878', padding: '8px 2px 4px' }}
                >
                  {t(language, 'All items', 'All items')}
                </p>
              )}
              {mobileProducts.rest.map(value => (
                <ProductCard
                  key={value.id}
                  product={value}
                  language={language}
                  onToggle={() => handleSelectProduct(value)}
                />
              ))}
              {!loading.product &&
                mobileProducts.pinned.length + mobileProducts.rest.length === 0 && (
                  <p
                    className="font-inter"
                    style={{ fontSize: '14px', color: '#6A7878', padding: '24px 0', textAlign: 'center' }}
                  >
                    {t(language, 'No Results Found', 'No Results Found')}
                  </p>
                )}
              <div ref={setLoaderRef} style={{ height: '1px' }}>
                {loading.product && (
                  <div className="w-full flex items-center justify-center" style={{ padding: '12px' }}>
                    <span className="loader"></span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <StickyFooter
          secondaryLabel={step === 1 ? t(language, 'Cancel', 'Cancel') : t(language, 'Back', 'Back')}
          onSecondary={handleBackNavigation}
          primaryLabel={step === 1 ? t(language, 'Next', 'Next') : t(language, 'Save', 'Save')}
          primaryProps={kb.primaryProps(step === 1 ? handleMobileNext : handleMobileSave)}
          note={
            step === 2
              ? `${listProductAttributes.length} ${t(language, 'selected', 'selected')}`
              : undefined
          }
        />
        <BaseAttributeSheet
          open={isBaseSheetOpen}
          onClose={() => setIsBaseSheetOpen(false)}
          language={language}
          options={availableBaseAttributes}
          selected={mobileBaseSelected}
          loading={loadingBaseAttributes}
          hasMore={baseAttributeHasMore}
          onSearch={handleSearchBaseAttributes}
          onLoadMore={handleLoadMoreBaseAttributes}
          onSelect={option => {
            if (!option) {
              clearBaseAttributeSelection();
            } else {
              setFormValues(prev => ({ ...prev, baseAttributeId: option.id }));
              setBaseAttributeValue(option.attributeName);
            }
            setIsBaseSheetOpen(false);
          }}
        />
        <ColorSheet
          open={colorSheetField !== null}
          title={
            colorSheetField === 'fontColor'
              ? t(language, 'POS font color', 'POS font color')
              : t(language, 'POS background color', 'POS background color')
          }
          language={language}
          value={colorSheetField === 'fontColor' ? posFont : posBackground}
          onChange={hex => {
            if (!colorSheetField) return;
            setPosColorsTouched(true);
            setFormValues(prev => ({ ...prev, [colorSheetField]: hex }));
          }}
          onClose={() => setColorSheetField(null)}
        />
        <ImagePreviewOverlay
          open={showPopupImage}
          src={logoSrc}
          onClose={() => setShowPopupImage(false)}
          closeLabel={t(language, 'Close', 'Close')}
        />
        <PopupLocation
          show={showPopup}
          title={
            id === 'new'
              ? t(language, 'Create Attributes', 'Create Attributes')
              : t(language, 'Update Attributes', 'Update Attributes')
          }
          desc={
            errors.missingFields
              ? errors.missingFields
              : id === 'new'
                ? language?.['Create Attributes Failed'] ?? 'Create Attributes Failed'
                : language?.['Update Attributes Failed'] ?? 'Update Items Failed'
          }
          onClose={handleClosePopup}
          onOpen={handleOpenPopup}
        />
      </div>
      </div>
    );
  }

  return (
    <div {...kb.scopeProps}>
      <div className="bg-white">
        <button type="button" className="flex items-center cursor-pointer" {...kb.stepProps(handleBackNavigation)}>
          <div className="w-6 h-6 flex items-center justify-center">
            <BackIcon />
          </div>
          <p
            style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
            className="font-inter"
          >
            {language && language?.['Back'] ? language?.['Back'] : 'Back'}
          </p>
        </button>
      </div>
      {id === 'new' ? (
        <p
          style={{
            margin: '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Add Attributes']
            ? language?.['Add Attributes']
            : 'Add Attributes'}
        </p>
      ) : (
        <p
          style={{
            margin: '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Edit Attributes']
            ? language?.['Edit Attributes']
            : 'Edit Attributes'}
        </p>
      )}
      <div className="flex items-start lg:items-center" style={{ gap: isMobile ? '10px' : '20px', marginTop: isMobile ? '2px' : '0', marginBottom: '24px' }}>
        <div
          className=""
          style={{
            width: isMobile ? 'calc(50% - 5px)' : 'calc(50% - 10px)',
          }}
        >
          <div
            className="w-full"
            style={{
              minHeight: '4px',
              width: "100%",
              backgroundColor: step === 1 || step === 2 ? '#0F7679' : '#DBE4E4',
              borderRadius: '20px',
            }}
          ></div>
          <div className="" style={{ marginTop: isMobile ? '6px' : '12px' }}>
            <div className="flex items-center" style={{ gap: '8px' }}>
              {step === 2 && (
                <div
                  className="flex items-center justify-center"
                  style={{ width: '20px', height: '20px' }}
                >
                  <CheckIcon />
                </div>
              )}
              <p
                className="font-medium text-[14px] font-inter leading-[20px]"
                style={{ color: '#0F7679' }}
              >
                {language && language?.['Step 1'] ? language?.['Step 1'] : 'Step 1'}
              </p>
            </div>
            <p
              className="font-medium text-[14px] font-inter leading-[20px]"
              style={{ color: '#05292A' }}
            >
              {language && language?.['Details'] ? language?.['Details'] : 'Details'}
            </p>
          </div>
        </div>
        <div
          className=""
          style={{
            width: isMobile ? 'calc(50% - 5px)' : 'calc(50% - 10px)',
          }}
        >
          <div
            className=""
            style={{
              minHeight: '4px',
              width: "100%",
              backgroundColor: step === 2 ? '#0F7679' : '#DBE4E4',
              borderRadius: '20px',
            }}
          ></div>
          <div className="" style={{ marginTop: isMobile ? '6px' : '12px' }}>
            <div className="flex items-center">
              <p
                className="font-medium text-[14px] font-inter leading-[20px]"
                style={{ color: '#0F7679' }}
              >
                {language && language?.['Step 2'] ? language?.['Step 2'] : 'Step 2'}
              </p>
            </div>
            <p
              className="font-medium text-[14px] font-inter leading-[20px]"
              style={{ color: '#05292A' }}
            >
              {language && language?.['Mappings'] ? language?.['Mappings'] : 'Mappings'}
            </p>
          </div>
        </div>
      </div>
      <div
        className="scroll-container"
        style={{
          height: isMobile ? 'calc(100% - 239px)' : 'calc(100% - 243px)',
          overflowY: 'scroll',
          paddingBottom:  '16px'
        }}
      >
        {step === 1 ? (
          <form onSubmit={handleSubmit}>
            <div>
              <div className="flex items-center" style={{ gap: '24px', flexWrap: 'wrap' }}>
                <div style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <p
                        className="font-medium text-[14px] font-inter leading-[20px]"
                        style={{ color: '#4A5353' }}
                      >
                        {language && language?.['Attribute Name']
                          ? language?.['Attribute Name']
                          : 'Attribute Name'}
                      </p>
                      <p
                        className="text-[14px] font-inter leading-[20px]"
                        style={{ color: '#BA0E13' }}
                      >
                        {language && language?.['*'] ? language?.['*'] : '*'}
                      </p>
                    </div>
                    <div className="w-full">
                      <input
                        required
                        type="text"
                        name="attributeName"
                        {...limitProps(limits, 'attributeName')}
                        value={formValues.attributeName}
                        onChange={handleChange}
                        onKeyDown={validateNoSpecialChars}
                        className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
                      />
                      <FieldCounter value={formValues.attributeName} max={limits.attributeName?.maxLength} />
                    </div>
                    {errors.attributeName && <p className="error">{errors.attributeName}</p>}
                  </div>
                </div>
                <div style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <p
                        className="font-medium text-[14px] font-inter leading-[20px]"
                        style={{ color: '#4A5353' }}
                      >
                        {language && language?.['Type'] ? language?.['Type'] : 'Type'}
                      </p>
                    </div>
                    <div className="w-full">
                      <DropdownSearch<{ name: string; value: number }>
                        refKey="type"
                        refs={{ type: typeRef }}
                        openStates={{ type: isTypeDropdownOpen }}
                        open={key => key === 'type' && setIsTypeDropdownOpen(true)}
                        close={key => key === 'type' && setIsTypeDropdownOpen(false)}
                        data={listPayment}
                        labelExtractor={item => item.name}
                        value={type}
                        setValue={setType}
                        onSelect={item => handleTypeSelect(item.name, item.value)}
                        error={errors.transactionMonthsId}
                        borderNone={false}
                      />
                    </div>
                    {errors.type && <p className="error">{errors.type}</p>}
                  </div>
                </div>
              </div>
              <div className="flex items-center" style={{ gap: '24px', flexWrap: 'wrap', marginTop: '20px', marginBottom: '32px' }}>
                <div style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <p
                        className="font-medium text-[14px] font-inter leading-[20px]"
                        style={{ color: '#4A5353' }}
                      >
                        {language && language?.['Display name']
                          ? language?.['Display name']
                          : 'Display name'}
                      </p>
                      <p
                        className="text-[14px] font-inter leading-[20px]"
                        style={{ color: '#BA0E13' }}
                      >
                        {language && language?.['*'] ? language?.['*'] : '*'}
                      </p>
                    </div>
                    <div className="w-full">
                      <input
                        type="text"
                        name="displayName"
                        {...limitProps(limits, 'displayName')}
                        value={formValues.displayName}
                        onChange={handleChange}
                        className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
                      />
                      <FieldCounter value={formValues.displayName} max={limits.displayName?.maxLength} />
                    </div>
                    {errors.displayName && <p className="error">{errors.displayName}</p>}
                  </div>
                </div>
                <div style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <p
                        className="font-medium text-[14px] font-inter leading-[20px]"
                        style={{ color: '#4A5353' }}
                      >
                        {language && language?.['Base Attribute']
                          ? language?.['Base Attribute']
                          : 'Base Attribute'}
                      </p>
                    </div>
                    <div className="w-full flex items-center" style={{ gap: '8px' }}>
                      <div className="flex-1 min-w-0">
                        <DropdownSearch<BaseAttributeOption>
                          refKey="baseAttribute"
                          refs={{ baseAttribute: baseAttributeRef }}
                          openStates={{ baseAttribute: isBaseAttributeDropdownOpen }}
                          open={key => key === 'baseAttribute' && setIsBaseAttributeDropdownOpen(true)}
                          close={key => key === 'baseAttribute' && setIsBaseAttributeDropdownOpen(false)}
                          data={availableBaseAttributes}
                          labelExtractor={item => item.attributeName}
                          value={baseAttributeValue}
                          setValue={setBaseAttributeValue}
                          onSelect={item => {
                            setFormValues(prev => ({
                              ...prev,
                              baseAttributeId: item.id,
                            }));
                          }}
                          onSearch={handleSearchBaseAttributes}
                          placeholder={language?.['Select base attribute'] ?? 'Select base attribute'}
                          borderNone={false}
                          loading={loadingBaseAttributes}
                          hasMore={baseAttributeHasMore}
                          onLoadMore={handleLoadMoreBaseAttributes}
                        />
                      </div>
                     
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ width: '100%', marginTop: '20px', marginBottom: '32px' }}>
                <div className="flex items-center" style={{ gap: '24px', flexWrap: 'wrap' }}>
                  <div style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <p
                          className="font-medium text-[14px] font-inter leading-[20px]"
                          style={{ color: '#4A5353' }}
                        >
                          {language && language?.['POS background color']
                            ? language?.['POS background color']
                            : 'POS background color'}
                        </p>
                      </div>
                      <div className="flex w-full items-center" style={{ gap: '12px' }}>
                        <input
                          type="color"
                          aria-label="POS background color"
                          value={formValues.backgroundColor || DEFAULT_POS_BG}
                          onChange={handlePosColorPicker('backgroundColor')}
                          className="h-12 w-14 shrink-0 cursor-pointer rounded-lg border border-[#EBEBED] bg-transparent p-0 appearance-none focus:outline-none focus:ring-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch-wrapper]:rounded-lg [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:rounded-lg [&::-moz-color-swatch]:border-0"
                        />
                      </div>
                    </div>
                  </div>
                  <div style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <p
                          className="font-medium text-[14px] font-inter leading-[20px]"
                          style={{ color: '#4A5353' }}
                        >
                          {language && language?.['POS font color']
                            ? language?.['POS font color']
                            : 'POS font color'}
                        </p>
                      </div>
                      <div className="flex w-full items-center" style={{ gap: '12px' }}>
                        <input
                          type="color"
                          aria-label="POS font color"
                          value={formValues.fontColor || DEFAULT_POS_FONT}
                          onChange={handlePosColorPicker('fontColor')}
                          className="h-12 w-14 shrink-0 cursor-pointer rounded-lg border border-[#EBEBED] bg-transparent p-0 appearance-none focus:outline-none focus:ring-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch-wrapper]:rounded-lg [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-0 [&::-moz-color-swatch]:rounded-lg [&::-moz-color-swatch]:border-0"
                        />
                        <div className="min-w-0 flex-1">
                          <input
                            type="text"
                            value={formValues.fontColor || ''}
                            onChange={handlePosColorHexInput('fontColor')}
                            onBlur={handlePosColorHexBlur('fontColor')}
                            placeholder={DEFAULT_POS_FONT}
                            className="text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-lg outline-none focus-none bg-transparent w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ width: '100%', marginTop: '20px' }}>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <p
                        className="font-medium text-[14px] font-inter leading-[20px]"
                        style={{ color: '#4A5353' }}
                      >
                        {language && language?.['POS preview']
                          ? language?.['POS preview']
                          : 'POS preview'}
                      </p>
                    </div>
                    <div>
                      <span
                          className="inline-block max-w-full rounded-md px-3 py-2 text-[14px] font-inter font-medium leading-[20px]"
                          style={{
                            backgroundColor:
                              normalizeHex(formValues.backgroundColor || DEFAULT_POS_BG) ||
                              DEFAULT_POS_BG,
                            color:
                              normalizeHex(formValues.fontColor || DEFAULT_POS_FONT) ||
                              DEFAULT_POS_FONT,
                          }}
                        >
                          {formValues.displayName?.trim() ||
                            (language && language?.['Display name']
                              ? language?.['Display name']
                              : 'Display name')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="lg:flex justify-between">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: '84px',
                    height: '84px',
                    borderRadius: '72px',
                    backgroundColor: '#EBF0F0',
                  }}
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    {previewImg || formValues?.image ? (
                      <img
                        src={previewImg || formValues?.image}
                        alt="Uploaded Preview"
                        style={{ height: '24px', objectFit: 'contain' }}
                      />
                    ) : (
                      <PhotoIcon />
                    )}
                  </div>
                </div>
                <div
                  className="w-full flex items-center justify-center"
                  style={{
                    padding: '20px',
                    border: `1px dashed ${isDragging ? '#3AC0C4' : '#DBE4E4'}`,
                    borderRadius: '16px',
                    width: isMobile ? '100%' : 'calc(100% - 100px)',
                  }}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div className="space-y-2 text-center">
                    <div className="flex items-center justify-center">
                      <UploadIcon />
                    </div>
                    {(!previewImg || formValues?.image) && (
                      <p className="text-[14px] text-main leading-[20px] font-inter font-medium">
                        {fileName
                          ? `File: ${fileName}`
                          : language && language?.['Drag and drop your logo here']
                            ? language?.['Drag and drop your logo here']
                            : 'Drag and drop your logo here'}
                      </p>
                    )}
                    {previewImg || formValues?.image ? (
                      <div className="flex items-center justify-center" style={{ gap: '24px' }}>
                        <button
                          type="button"
                          className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer"
                          style={{
                            color: '#3AC0C4',
                            textDecoration: 'underline',
                            marginTop: '16px',
                          }}
                          onClick={() => {
                            setShowPopupImage(true);
                          }}
                        >
                          View Files
                        </button>
                        <button
                          type="button"
                          className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer"
                          style={{
                            color: '#3AC0C4',
                            textDecoration: 'underline',
                            marginTop: '16px',
                          }}
                          onClick={() => {
                            handleRemoveFile();
                          }}
                        >
                          Remove FIle
                        </button>
                      </div>
                    ) : (
                      <>
                        <p
                          style={{ fontSize: '16px', color: '#819596', marginBottom: '8px' }}
                          className=" text-main leading-[20px] font-inter"
                        >
                          {language && language?.['OR'] ? language?.['OR'] : 'OR'}
                        </p>
                        <label
                          htmlFor="fileInput"
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === ' ' || e.key === 'Enter') {
                              e.preventDefault();
                              document.getElementById('fileInput')?.click();
                            }
                          }}
                          className="text-[14px] font-inter font-semibold leading-[18px] cursor-pointer"
                          style={{
                            color: '#0F7679',
                            padding: '8px 16px',
                            border: '1px solid #C9D7D8',
                            borderRadius: '30px',
                          }}
                        >
                          {language && language?.['Browse file']
                            ? language?.['Browse file']
                            : 'Browse file'}
                        </label>
                      </>
                    )}

                    <input
                      type="file"
                      accept=".jpeg,.jpg,.heic,.png,.webp"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      id="fileInput"
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className="h-full">
            <div style={{ marginBottom: '20px' }}>
              <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                <p style={{ fontSize: '20px', fontWeight: '600', color: '#05292A' }}>
                  {language && language?.['All items'] ? language?.['All items'] : 'All items'}
                </p>
                <div className="flex items-center" style={{ gap: '16px' }}>
                  {!isMobile && (
                    <div className="flex items-center gap-x-2">
                      <div className="relative w-[400px] h-12">
                        <input
                          ref={searchInputRef}
                          style={{
                            borderColor: '#E7F1F1',
                            padding: '8px 36px',
                            minWidth: '332px',
                            borderRadius: '12px',
                            border: '1px solid #C9D7D8',
                            color: '#05292A',
                          }}
                          name="searchValue"
                          onKeyDown={handleSubmitSearch}
                          value={filter.searchValue}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setFilter((prev: any) => ({ ...prev, searchValue: e.target.value }));
                          }}
                          className="text-[14px] font-inter w-full h-full rounded-md outline-none focus:none bg-white bg-transparent"
                        />
                        <div className="absolute top-1/2 -translate-y-1/2" style={{ left: '8px' }}>
                          <SearchIcon />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center cursor-pointer" style={{ gap: 8 }}>
                    <div className="flex items-center" style={{ borderRadius: "12px", border: '1px solid #DBE4E4', padding: "8px 8px 8px 12px", height: 40, width: 120 }}>
                      <div className="relative" ref={filterCorrectionsRef}>
                        <div className="relative">
                          <input
                            type="text"
                            role="combobox"
                            aria-expanded={isFilterTypeDropdownOpen}
                            value={filterType}
                            style={{ color: "#6A7878", fontSize: 14, fontWeight: '500', padding: 0 }}
                            onChange={(e) => {
                              setFilterType(e.target.value);
                              setIsFilterTypeDropdownOpen(true);
                            }}
                            onClick={() => setIsFilterTypeDropdownOpen(true)}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                e.preventDefault();
                                const n = listTypeFilter.length;
                                const down = e.key === 'ArrowDown';
                                const from = isFilterTypeDropdownOpen ? filterTypeHighlight : -1;
                                setFilterTypeHighlight(from < 0 ? (down ? 0 : n - 1) : (from + (down ? 1 : n - 1)) % n);
                                setIsFilterTypeDropdownOpen(true);
                              } else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                                // A list filter: Enter picks the highlighted option, never saves the page.
                                e.preventDefault();
                                if (isFilterTypeDropdownOpen && filterTypeHighlight >= 0) {
                                  handleFilterTypeSelect(listTypeFilter[filterTypeHighlight].name);
                                }
                              } else if (e.key === 'Escape' || e.key === 'Tab' || e.key === 'Enter') {
                                setIsFilterTypeDropdownOpen(false);
                              }
                            }}
                            className="font-inter placeholder:text-placeholder text-main p-3 outline-none focus-none bg-transparent w-full"
                          />
                          <div
                            className="absolute flex items-center justify-center w-6 h-6"
                            style={{ top: "50%", right: 0, transform: "translateY(-50%)" }}
                          >
                            {filterType === "All" ?
                              <DropdownIcon />
                              :
                              <WhiteDropdownIcon />
                            }
                          </div>
                        </div>
                        {isFilterTypeDropdownOpen && (
                          <div
                            role="listbox"
                            style={{ width: "100px", left: '-10px', top: '30px' }}
                            className="absolute z-[100] mt-1 bg-white border border-[#EBEBED] rounded-lg max-h-60 overflow-auto p-1 shadow-extend">
                            {listTypeFilter
                              .map((status: any, index: number) => (
                                <div
                                  key={status?.id}
                                  role="option"
                                  aria-selected={filterType === status?.name}
                                  style={{ padding: "8px 6px", borderRadius: "4px", color: "#6A7878", fontSize: 14, fontWeight: '500', ...(index === filterTypeHighlight ? { backgroundColor: '#E7F1F1' } : null) }}
                                  className="cursor-pointer hover:bg-[#E7F1F1] text-main text-[14px] font-inter duration-300"
                                  onMouseDown={e => e.preventDefault()}
                                  onClick={() => handleFilterTypeSelect(status?.name)}
                                >
                                  {language?.[status?.name] ?? status?.name}
                                </div>
                              ))
                            }
                          </div>
                        )}
                      </div>
                    </div>
                    {filter.searchValue !== '' && (
                      <button
                        type="button"
                        onClick={() => {
                          nextPageToFetchRef.current = 1;
                          hasLoadedPageRef.current.clear();
                          setHasMore(prev => ({ ...prev, product: true }));
                          fetchProducts('', listProductAttributesRef.current, 1);
                        }}
                        className="flex items-center justify-center font-inter cursor-pointer"
                        style={{ width: 100, height: 36, fontSize: 14, color: '#6E6E6E' }}
                      >
                        <p>{language?.['Clear filters'] ?? 'Clear filters'}</p>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {isMobile && (
                <div className="flex items-center gap-x-2" style={{ marginTop: '12px' }}>
                  <div className="relative w-[400px] h-12">
                    <input
                      ref={searchInputRef}
                      style={{
                        borderColor: '#E7F1F1',
                        padding: '8px 36px',
                        minWidth: '332px',
                        borderRadius: '12px',
                        border: '1px solid #C9D7D8',
                        color: '#05292A',
                      }}
                      name="searchValue"
                      onKeyDown={handleSubmitSearch}
                      value={filter.searchValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFilter((prev: any) => ({ ...prev, searchValue: e.target.value }));
                      }}
                      className="text-[14px] font-inter w-full h-full rounded-md outline-none focus:none bg-white bg-transparent"
                    />
                    <div className="absolute top-1/2 -translate-y-1/2" style={{ left: '8px' }}>
                      <SearchIcon />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              ref={scrollRef}
              className="h-full w-full flex bg-white scroll-container"
              style={{
                gap: isMobile ? '12px' : '20px',
                overflowY: 'scroll',
                flexWrap: 'wrap',
                height: 'calc(100% - 103px)',
              }}
            >
              {listProducts
                .filter(val => filterType === 'All' || val.isAttributes)
                .map((value, index) => {
                  return (
                    <div
                      key={value.id}
                      className=""
                      style={{
                        width: !isMobile ? 'calc(24% - 10px)' : 'calc(50% - 6px)',
                        border: value.isAttributes ? '2px solid #0F7679' : '1px solid #EBF0F0',
                        borderRadius: 16,
                        padding: '16px',
                        backgroundColor: value.isAttributes ? '#0F767914' : '#F8F8F8',
                        height: '108px',
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div
                          role="checkbox"
                          aria-checked={!!value.isAttributes}
                          aria-label={value.productName}
                          tabIndex={0}
                          className="cursor-pointer w-5 h-5 flex items-center justify-center"
                          onClick={() => {
                            handleSelectProduct(value);
                          }}
                          onKeyDown={e => {
                            // Space toggles, as on a native checkbox; Enter is left to the form.
                            if (e.key !== ' ' || e.ctrlKey || e.metaKey || e.altKey) return;
                            e.preventDefault();
                            if (!e.repeat) handleSelectProduct(value);
                          }}
                          style={{
                            border: value.isAttributes ? '' : '1px solid #C9D7D8',
                            backgroundColor: value.isAttributes ? '#0F7679' : 'transparent',
                            borderRadius: '6px',
                          }}
                        >
                          {value.isAttributes ? <WhiteCheckIcon /> : <></>}
                        </div>
                        <div
                          className="flex items-center gap-x-2 font-inter font-medium"
                          style={{
                            fontSize: '12px',
                            borderRadius: '100px',
                            height: '26px',
                            padding: '5px 8px',
                            color: '#007C46',
                            backgroundColor: '#007C461F',
                          }}
                        >
                          <div
                            style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '100%',
                              backgroundColor: '#007C46',
                            }}
                          ></div>
                          {value.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </div>
                      <div className="space-y-1" style={{ marginTop: '12px' }}>
                        <p
                          className="font-inter"
                          style={{
                            fontSize: '16px',
                            lineHeight: '20px',
                            color: '#222525',
                            fontWeight: 600,
                          }}
                        >
                          {value.productName}
                        </p>
                        <p className="font-inter" style={{ fontSize: "14px", lineHeight: "18px", color: "#4A5353" }}>{value.sku}</p>
                      </div>
                    </div>
                  );
                })}
              <div ref={setLoaderRef} style={{ height: '1px' }}>
                {loading.product && (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="loader"></span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <div
        className="w-full flex items-center justify-end shadow_button relative"
        style={{ gap: "12px", paddingTop: "24px", }}
      >
        <div className="absolute" style={{ left: "-24px", top: 0, height: "1px", backgroundColor: "#DBE4E4", width: isMobile ? 'calc(100vw + 32px)' : "calc(-256px + 48px + 100vw)" }}></div>
        <button
          type="button"
          {...kb.stepProps(handleBackNavigation)}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            color: '#222525',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #C9D7D8',
          }}
          className="font-inter cursor-pointer"
        >
          {step === 1 ?
            language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'
            :
            language && language?.['Back'] ? language?.['Back'] : 'Back'
          }
        </button>
        {step === 1 ? (
          <button
            type="button"
            {...kb.primaryProps(() => setStep(2))}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#0F7679',
            }}
            className="font-inter cursor-pointer"
          >
            {language && language?.['Next'] ? language?.['Next'] : 'Next'}
          </button>
        ) : (
          <button
            type="button"
            {...kb.primaryProps(handleSubmit)}
            style={{
              padding: '10px 24px',
              borderRadius: '30px',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: '#0F7679',
            }}
            className="font-inter cursor-pointer"
          >
            {language && language?.['Save'] ? language?.['Save'] : 'Save'}
          </button>
        )}
      </div>
      <div
        className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showPopupImage ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          } transition-opacity duration-300 ease-in-out`}
        ref={imageDialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={language?.['View Files'] ?? 'View Files'}
        style={{
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: '200',
          transition: 'opacity 0.3s ease, visibility 0.3s ease',
          width: '100%',
          visibility: showPopupImage ? 'visible' : 'hidden',
        }}
      >
        <div
          className={`transform transition-transform duration-300 ease-in-out ${showPopupImage ? 'translate-y-0' : 'translate-y-4 opacity-0'
            }`}
          style={{
            borderRadius: '6px',
            backgroundColor: 'white',
            width: '80%',
            height: '90%',
            transition: 'transform 0.3s ease, opacity 0.3s ease',
            overflow: 'hidden',
          }}
        >
          <div
            className="flex items-center justify-between"
            style={{
              padding: '12px 16px',
              backgroundColor: '#F7F7F8',
              borderBottom: '1px solid #EBEBED',
            }}
          >
            <p className="text-[16px] font-semibold font-inter text-main leading-[24px]"></p>
            <button
              type="button"
              aria-label={language?.['Close'] ?? 'Close'}
              onClick={() => {
                setShowPopupImage(false);
              }}
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '24px',
                height: '24px',
              }}
            >
              <CloseIcon />
            </button>
          </div>
          <div
            className="flex items-center justify-center"
            style={{ padding: '20px 16px', height: 'calc(100% - 48px)' }}
          >
            {(previewImg || formValues?.image) && (
              <img
                src={previewImg ? previewImg : formValues?.image}
                alt="Uploaded preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '4px',
                }}
              />
            )}
          </div>
        </div>
      </div>
      <PopupLocation
        show={showPopup}
        title={`${id === 'new'
          ? language && language?.['Create Attributes']
            ? language?.['Create Attributes']
            : 'Create Attributes'
          : language && language?.['Update Attributes']
            ? language?.['Update Attributes']
            : 'Update Attributes'
          }`}
        desc={
          errors.missingFields
            ? errors.missingFields
            : id === 'new'
              ? language?.['Create Attributes Failed'] ?? 'Create Attributes Failed'
              : language?.['Update Attributes Failed'] ?? 'Update Items Failed'
        }
        onClose={handleClosePopup}
        onOpen={handleOpenPopup}
      />
    </div>
  );
};

export default CreateAttribuites;
